import React, { useEffect, useState } from 'react';
import { getUserNotifications, getUnreadNotificationCount, markNotificationAsRead } from '../../redux/api';
import notificationService from '../../services/notificationService';
import { toast } from 'react-toastify';
import { useWebSocket } from '../../contexts/WebSocketContext';

/**
 * NotificationChecker component
 * Checks for scheduled notifications and displays them
 * Should be mounted once in the app (e.g., in App.js)
 */
const NotificationChecker = () => {
  const [checkedNotifications, setCheckedNotifications] = useState(new Set());
  const { socket } = useWebSocket();
  const checkedNotificationsRef = React.useRef(checkedNotifications);
  const HANDOFF_PREFIX = 'handoff_';

  useEffect(() => {
    checkedNotificationsRef.current = checkedNotifications;
  }, [checkedNotifications]);

  useEffect(() => {
    // Check if user is logged in before setting up anything
    const profile = localStorage.getItem('profile');
    if (!profile) {
      return; // User not logged in, don't set up anything
    }

    // Request notification permission on mount
    if (notificationService.isSupported()) {
      notificationService.requestPermission().catch(console.error);
    }

    // Check for notifications immediately
    checkScheduledNotifications();

    // Check every minute for scheduled notifications
    const interval = setInterval(() => {
      // Check again if user is still logged in
      const currentProfile = localStorage.getItem('profile');
      if (!currentProfile) {
        clearInterval(interval);
        return;
      }
      checkScheduledNotifications();
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handler = (notification) => {
      try {
        const id = notification?._id;
        if (!id) return;

        if (checkedNotificationsRef.current.has(id)) {
          return;
        }

        const next = new Set(checkedNotificationsRef.current);
        next.add(id);
        checkedNotificationsRef.current = next;
        setCheckedNotifications(next);

        // Immediately show browser notification + toast
        // showNotification expects full notification shape.
        showNotification(notification);
      } catch (e) {
        // Silent fail to avoid breaking notification stream
        console.error('Socket notification handler error:', e);
      }
    };

    socket.on('user_notification_created', handler);
    return () => {
      socket.off('user_notification_created', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const checkScheduledNotifications = async () => {
    // Check if user is logged in before making API call
    const profile = localStorage.getItem('profile');
    if (!profile) {
      return; // User not logged in, skip notification check
    }
    
    try {
      // Get all unread notifications
      const response = await getUserNotifications({ unreadOnly: 'true', limit: 100 });
      const notifications = response.data.notifications || [];

      const now = new Date();

      // Check each notification
      notifications.forEach(async (notification) => {
        // Parking: samo modal pri pokretanju rada (JobStatusControls), ne poll/toast — korisnik dobija poruku od parking servisa
        if (notification.type === 'parking') return;

        // Skip if we've already shown this notification
        const notifId = notification?._id;
        if (!notifId) return;

        if (checkedNotificationsRef.current.has(notifId)) {
          return;
        }

        // Check if it's a scheduled notification that should be shown now
        if (notification.scheduledFor) {
          const scheduledTime = new Date(notification.scheduledFor);
          const timeDiff = now.getTime() - scheduledTime.getTime();

          // Show if scheduled time has passed (within last 5 minutes to avoid duplicates)
          if (timeDiff >= 0 && timeDiff < 5 * 60 * 1000) {
            await showNotification(notification);
            checkedNotificationsRef.current = new Set([...checkedNotificationsRef.current, notifId]);
            setCheckedNotifications(checkedNotificationsRef.current);
          }
        } else if (!notification.isRead) {
          // Show unscheduled unread notifications immediately (but only once)
          if (!checkedNotificationsRef.current.has(notifId)) {
            await showNotification(notification);
            checkedNotificationsRef.current = new Set([...checkedNotificationsRef.current, notifId]);
            setCheckedNotifications(checkedNotificationsRef.current);
          }
        }
      });
    } catch (error) {
      console.error('Error checking scheduled notifications:', error);
    }
  };

  const showNotification = async (notification) => {
    try {
      if (notification.type === 'parking') return;

      const metadataEvent = notification?.metadata?.event || notification?.metadata?.['event'] || '';
      const isHandoffNotification = typeof metadataEvent === 'string' && metadataEvent.startsWith(HANDOFF_PREFIX);

      await notificationService.showNotification(notification.title, {
        body: notification.message,
        icon: '/logo192.png',
        tag: `notification-${notification._id}`,
        requireInteraction: notification.priority === 'urgent' || notification.priority === 'high',
        sound: true,
        soundType: notification.priority === 'urgent' ? 'alert' : 'beep',
        onClick: () => {
          window.focus();
        },
        data: {
          notificationId: notification._id,
          type: notification.type
        }
      });

      const toastType = notification.type === 'error' ? 'error' :
                        notification.type === 'success' ? 'success' :
                        notification.type === 'warning' ? 'warning' : 'info';

      toast[toastType](notification.message, {
        position: 'top-center',
        autoClose: notification.priority === 'urgent' ? 15000 : 10000,
        closeOnClick: true,
        pauseOnHover: true
      });

      if (isHandoffNotification && notification._id && !notification.isRead) {
        try {
          await markNotificationAsRead(notification._id);
        } catch (e) {
          // Silent fail
        }
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  };

  return null; // This component doesn't render anything
};

export default NotificationChecker;

